import React, { useContext } from 'react';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Helmet } from 'react-helmet-async';
import { MailOpen } from 'lucide-react';
import UserService from '../../services/UserService/UserService';
import UserContext from '../../context/UserContext/UserContext';
import AuthService from '../../services/AuthService/AuthService';
import Button from '../../components/Button/Button';
import CustomInput from '../../components/CustomInput/CustomInput';
import AuthLayout from '../../components/AuthLayout/AuthLayout';
import AuthErrorAlert from '../../components/AuthLayout/AuthErrorAlert';
import BackToLoginLink from '../../components/AuthLayout/BackToLoginLink';

interface FormValues {
  email: string;
}

const CONTACT_URL = 'https://www.japeto.ai/contact/';
const GENERIC_ERROR = 'Something went wrong. Please try again later.';

const HEADING = 'm-0 text-center text-2xl font-extrabold tracking-[-0.5px]';
const SUBTITLE =
  'mx-0 mb-0 mt-2.5 text-center text-sm leading-[1.55] text-auth-muted';
const INLINE_LINK = 'font-bold text-primary underline-offset-4 hover:underline';

function ForgotPasswordPage(): React.ReactElement {
  const [sentReset, setSentReset] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>('');
  const { user } = useContext(UserContext);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address')
  });

  React.useEffect(() => {
    if (user) {
      AuthService.logout();
    }
  }, [user]);

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = (data: FormValues): void => {
    setIsLoading(true);
    setErrorMessage('');
    setEmail(data.email);
    UserService.resetPassword(data.email)
      .then(() => setSentReset(true))
      .catch((err) => {
        if (err.name === 'CanceledError') return;
        setErrorMessage(err.apiError?.message || GENERIC_ERROR);
      })
      .finally(() => setIsLoading(false));
  };

  const supportLine = (
    <p className='mx-0 mb-0 mt-[22px] text-center text-[13px] text-auth-muted'>
      Did your email change or having another issue logging in?{' '}
      <a
        href={CONTACT_URL}
        target='_blank'
        rel='noopener noreferrer'
        className={INLINE_LINK}
      >
        Contact our support team
      </a>
    </p>
  );

  const renderRequestForm = (): React.ReactElement => (
    <>
      <h1 className={HEADING}>Reset your password</h1>
      <p className={SUBTITLE}>
        Enter the email for your account and we&apos;ll send you a password
        reset link.
      </p>
      <form
        className='mt-[26px] flex flex-col gap-3'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Controller
          name='email'
          control={control}
          render={({ field }) => (
            <CustomInput
              {...field}
              type='email'
              label='Email'
              autoComplete='email'
              inputSize='xl'
              containerClassName=''
              error={errors.email}
              fullWidth
            />
          )}
        />
        <AuthErrorAlert message={errorMessage} />
        <Button
          type='submit'
          loading={isLoading}
          size='xl'
          rounded='xl'
          className='mt-1 w-full'
        >
          Send reset link
        </Button>
      </form>
      {supportLine}
    </>
  );

  const renderSentConfirmation = (): React.ReactElement => (
    <>
      <div className='mx-auto flex h-[46px] w-[46px] items-center justify-center rounded-xl bg-auth-chip'>
        <MailOpen className='h-6 w-6 text-brand-light' aria-hidden />
      </div>
      <h1 className={`${HEADING} mt-4`}>Check your email</h1>
      <p className={SUBTITLE}>
        If we have an account for{' '}
        <strong className='text-auth-ink'>{email}</strong>, a reset link is on
        its way. If you don&apos;t receive it shortly, check your spam folder.
      </p>
      <AuthErrorAlert message={errorMessage} />
      <Button
        type='button'
        loading={isLoading}
        size='xl'
        rounded='xl'
        className='mt-[26px] w-full'
        onClick={() => onSubmit({ email })}
      >
        Resend link
      </Button>
      {supportLine}
    </>
  );

  return (
    <AuthLayout belowCard={<BackToLoginLink />}>
      <Helmet>
        <title>Japeto - Forgot Password</title>
      </Helmet>
      {sentReset ? renderSentConfirmation() : renderRequestForm()}
    </AuthLayout>
  );
}

export default ForgotPasswordPage;
